// src/components/Troubleshooting.js
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Modal, Badge, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function Troubleshooting() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [userRole, setUserRole] = useState(localStorage.getItem('role'));
  const [userId, setUserId] = useState(localStorage.getItem('userId'));

  // Form states
  const [ticketForm, setTicketForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  const [responseForm, setResponseForm] = useState({
    response: ''
  });

  // Load tickets on component mount
  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to access troubleshooting');
      return;
    }
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/tickets');
      console.log('Tickets API response:', response.data); // Debug log
      // Ensure tickets is always an array
      setTickets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading tickets:', error);
      console.error('Error response:', error.response); // Debug log
      if (error.response?.status === 401) {
        toast.error('Please log in to view tickets');
      } else if (error.response?.status === 403) {
        toast.error('Access denied');
      } else {
        toast.error('Failed to load tickets');
      }
      setTickets([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/tickets', {
        ...ticketForm,
        userId: userId
      });
      
      // Ensure we're adding to an array
      const newTickets = Array.isArray(tickets) ? tickets : [];
      setTickets([response.data, ...newTickets]);
      setShowCreateModal(false);
      setTicketForm({ title: '', description: '', priority: 'medium', category: 'general' });
      toast.success('Ticket created successfully');
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket');
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/tickets/${selectedTicket._id}/respond`, {
        response: responseForm.response,
        adminId: userId
      });
      
      // Update the ticket in the list - ensure tickets is an array
      const currentTickets = Array.isArray(tickets) ? tickets : [];
      setTickets(currentTickets.map(ticket => 
        ticket._id === selectedTicket._id ? response.data : ticket
      ));
      
      setShowResponseModal(false);
      setSelectedTicket(null);
      setResponseForm({ response: '' });
      toast.success('Response submitted successfully');
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Failed to submit response');
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'secondary',
      medium: 'warning',
      high: 'danger',
      urgent: 'dark'
    };
    return <Badge bg={variants[priority]}>{priority.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'danger',
      'in-progress': 'warning',
      resolved: 'success',
      closed: 'secondary'
    };
    return <Badge bg={variants[status]}>{status.replace('-', ' ').toUpperCase()}</Badge>;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      general: '❓',
      technical: '🔧',
      billing: '💰',
      account: '👤',
      booking: '📅',
      payment: '💳'
    };
    return icons[category] || '❓';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="sticky-top bg-white border-bottom pb-3" style={{ 
        zIndex: 1020,
        top: '56px'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h2 className="mb-0">Troubleshooting</h2>
            <p className="text-muted mb-0">Support ticket management system</p>
          </div>
          <div className="d-flex gap-2">
            {(userRole === 'professional' || userRole === 'listing_only') && (
              <Button 
                variant="primary" 
                onClick={() => setShowCreateModal(true)}
              >
                Create New Ticket
              </Button>
            )}
            <Badge bg="info" className="fs-6">
              {tickets.length} Tickets
            </Badge>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12">
          {(!tickets || tickets.length === 0) ? (
            <Card>
              <Card.Body className="text-center py-5">
                <div className="mb-3">
                  <span className="fs-1">🎫</span>
                </div>
                <h4>No tickets found</h4>
                <p className="text-muted">
                  {(userRole === 'professional' || userRole === 'listing_only')
                    ? 'Create your first support ticket to get help with any issues.'
                    : 'No support tickets have been created yet.'
                  }
                </p>
                {(userRole === 'professional' || userRole === 'listing_only') && (
                  <Button 
                    variant="primary" 
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create First Ticket
                  </Button>
                )}
              </Card.Body>
            </Card>
          ) : (
            <div className="row">
              {(Array.isArray(tickets) ? tickets : []).map((ticket) => (
                <div key={ticket._id} className="col-md-6 col-lg-4 mb-3">
                  <Card className="h-100">
                    <Card.Header className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center">
                        <span className="me-2 fs-5">{getCategoryIcon(ticket.category)}</span>
                        <div>
                          <h6 className="mb-0">{ticket.title}</h6>
                          <small className="text-muted">#{ticket._id.slice(-6)}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        {getPriorityBadge(ticket.priority)}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <p className="text-muted small mb-1">
                          {ticket.description.length > 100 
                            ? `${ticket.description.substring(0, 100)}...` 
                            : ticket.description
                          }
                        </p>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          Created: {formatDate(ticket.createdAt)}
                        </small>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          By: {ticket.userName || 'Unknown User'}
                        </small>
                        {userRole === 'admin' && ticket.status !== 'closed' && (
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setShowResponseModal(true);
                            }}
                          >
                            Respond
                          </Button>
                        )}
                        {(userRole === 'professional' || userRole === 'listing_only') && ticket.status === 'open' && (
                          <Button 
                            size="sm" 
                            variant="outline-secondary"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setShowResponseModal(true);
                            }}
                          >
                            View Details
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Ticket Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Support Ticket</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTicket}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={ticketForm.title}
                onChange={(e) => setTicketForm({...ticketForm, title: e.target.value})}
                placeholder="Brief description of the issue"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={ticketForm.category}
                onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
              >
                <option value="general">General</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing</option>
                <option value="account">Account</option>
                <option value="booking">Booking</option>
                <option value="payment">Payment</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={ticketForm.priority}
                onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={ticketForm.description}
                onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                placeholder="Please provide detailed information about your issue..."
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Ticket
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Response Modal */}
      <Modal show={showResponseModal} onHide={() => setShowResponseModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {userRole === 'admin' ? 'Respond to Ticket' : 'Ticket Details'}
          </Modal.Title>
        </Modal.Header>
        {selectedTicket && (
          <>
            <Modal.Body>
              <div className="mb-4">
                <h5>{selectedTicket.title}</h5>
                <div className="d-flex gap-2 mb-2">
                  {getPriorityBadge(selectedTicket.priority)}
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <p className="text-muted small">
                  Created by {selectedTicket.userName} on {formatDate(selectedTicket.createdAt)}
                </p>
                <div className="bg-light p-3 rounded">
                  <strong>Issue Description:</strong>
                  <p className="mb-0 mt-2">{selectedTicket.description}</p>
                </div>
              </div>

              {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                <div className="mb-4">
                  <h6>Previous Responses:</h6>
                  {selectedTicket.responses.map((response, index) => (
                    <div key={index} className="border-start border-primary ps-3 mb-3">
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">
                          {response.adminName || 'Admin'} - {formatDate(response.createdAt)}
                        </small>
                      </div>
                      <p className="mb-0 mt-1">{response.response}</p>
                    </div>
                  ))}
                </div>
              )}

              {userRole === 'admin' && selectedTicket.status !== 'closed' && (
                <Form onSubmit={handleSubmitResponse}>
                  <Form.Group>
                    <Form.Label>Your Response *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={responseForm.response}
                      onChange={(e) => setResponseForm({...responseForm, response: e.target.value})}
                      placeholder="Enter your response to the user..."
                      required
                    />
                  </Form.Group>
                </Form>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowResponseModal(false)}>
                Close
              </Button>
              {userRole === 'admin' && selectedTicket.status !== 'closed' && (
                <Button variant="primary" onClick={handleSubmitResponse}>
                  Submit Response
                </Button>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>

      <ToastContainer position="top-right" />
    </div>
  );
} 